import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    output,
} from '@angular/core';
import type {
    FilePreviewAction,
    FilePreviewInfoPanelConfig,
    FilePreviewPanelConfig,
    PreviewZoom,
} from '../models';
import { FILE_PREVIEW_PANEL_SIDE } from '../models';
import {
    DOCUMENT_INFO_PANEL_ID,
    PreviewPanel,
    PreviewPanelState,
    PREVIEW_PANEL_STATE_PROVIDER,
    resolvePreviewPanelDomId,
} from '../preview-controls';
import { Controls } from './controls';
import { ImageRenderer } from './image-renderer';
import { UiState as ImageUiState } from './ui-state';

@Component({
    selector: 'qpx-image-preview',
    imports: [Controls, ImageRenderer, PreviewPanel],
    templateUrl: './image-preview.html',
    providers: [ImageUiState, PREVIEW_PANEL_STATE_PROVIDER],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'contents',
    },
})
export class ImagePreview {
    //#region Dependency injection
    private readonly state = inject(ImageUiState);
    private readonly panelState = inject(PreviewPanelState);
    //#endregion

    //#region Inputs
    public readonly previewUrl = input.required<string>();
    public readonly fileName = input.required<string>();
    public readonly actions = input<readonly FilePreviewAction[]>([]);
    public readonly panels = input<readonly FilePreviewPanelConfig[]>([]);
    public readonly panelsMountedByHost = input(false);
    public readonly infoPanel = input<FilePreviewInfoPanelConfig | undefined>(
        undefined
    );
    public readonly previewHeight = input.required<string>();
    //#endregion

    //#region Outputs
    public readonly viewerLoaded = output<void>();
    public readonly viewerLoadError = output<Error | ErrorEvent | Event>();
    public readonly closePreview = output<void>();
    //#endregion

    //#region State
    protected readonly FILE_PREVIEW_PANEL_SIDE = FILE_PREVIEW_PANEL_SIDE;
    protected readonly documentInfoPanelId = resolvePreviewPanelDomId(
        FILE_PREVIEW_PANEL_SIDE.Right,
        DOCUMENT_INFO_PANEL_ID
    );
    protected readonly controlsVisible = this.state.controlsVisible;
    protected readonly activeLeftPanelId = this.panelState.activeLeftPanelId;
    protected readonly activeRightPanelId = this.panelState.activeRightPanelId;
    protected readonly leftPanels = computed(() =>
        this.panels().filter(
            (panel) => panel.side === FILE_PREVIEW_PANEL_SIDE.Left
        )
    );
    protected readonly rightPanels = computed(() =>
        this.panels().filter(
            (panel) => panel.side === FILE_PREVIEW_PANEL_SIDE.Right
        )
    );
    protected readonly activeLeftPanel = computed(() =>
        this.leftPanels().find((panel) => panel.id === this.activeLeftPanelId())
    );
    protected readonly activeRightPanel = computed(() =>
        this.rightPanels().find(
            (panel) => panel.id === this.activeRightPanelId()
        )
    );
    protected readonly documentInfoVisible = computed(
        () => this.activeRightPanelId() === DOCUMENT_INFO_PANEL_ID
    );
    protected readonly zoom = this.state.zoom;
    protected readonly handTool = this.state.handTool;
    protected readonly imageWidth = this.state.width;
    protected readonly imageMaxWidth = this.state.maxWidth;
    protected readonly imageMaxHeight = this.state.maxHeight;
    protected readonly imageRotation = this.state.rotation;
    //#endregion

    //#region Lifecycle hooks
    constructor() {
        effect(() => {
            this.state.setParams({
                previewUrl: this.previewUrl(),
                fileName: this.fileName(),
            });
        });

        effect(() => {
            this.panelState.setParams({
                previewUrl: this.previewUrl(),
                fileName: this.fileName(),
                panels: this.panels(),
            });
            this.panelState.setControlsVisible(this.controlsVisible());
        });
    }
    //#endregion

    //#region Template methods
    protected onImageZoomOut(): void {
        this.state.zoomOut();
    }

    protected onImageZoomIn(): void {
        this.state.zoomIn();
    }

    protected onImageZoomReset(): void {
        this.state.resetZoom();
    }

    protected onImageZoomSelect(zoom: PreviewZoom): void {
        this.state.selectZoom(zoom);
    }

    protected onImageRotateClockwise(): void {
        this.state.rotateClockwise();
    }

    protected onImageHandToolToggle(): void {
        this.state.toggleHandTool();
    }

    protected onControlsToggle(): void {
        this.state.toggleControls();
        if (!this.controlsVisible()) {
            this.panelState.closeAllPanels();
        }
    }

    protected onDocumentInfoToggle(): void {
        this.panelState.toggleDocumentInfo();
    }

    protected onPanelToggle(panel: FilePreviewPanelConfig): void {
        this.panelState.togglePanel(panel);
    }

    protected panelId(panel: FilePreviewPanelConfig): string {
        return resolvePreviewPanelDomId(panel.side, panel.id);
    }

    protected async onImageDownload(): Promise<void> {
        const url = this.previewUrl();
        if (!url) return;

        try {
            const response = await fetch(url);
            if (!response.ok) {
                this.downloadUrl(url, this.fileName());
                return;
            }

            this.downloadBlob(await response.blob(), this.fileName());
        } catch {
            this.downloadUrl(url, this.fileName());
        }
    }

    protected onImagePointerDown(event: PointerEvent): void {
        if (!this.handTool()) return;

        const target = event.currentTarget;
        if (!(target instanceof HTMLElement)) return;

        target.setPointerCapture(event.pointerId);
        this.state.panState = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            scrollLeft: target.scrollLeft,
            scrollTop: target.scrollTop,
        };
    }

    protected onImagePointerMove(event: PointerEvent): void {
        if (!this.handTool()) return;

        const panState = this.state.panState;
        if (!panState || panState.pointerId !== event.pointerId) return;

        const target = event.currentTarget;
        if (!(target instanceof HTMLElement)) return;

        target.scrollLeft =
            panState.scrollLeft - (event.clientX - panState.startX);
        target.scrollTop =
            panState.scrollTop - (event.clientY - panState.startY);
    }

    protected onImagePointerUp(event: PointerEvent): void {
        if (this.state.panState?.pointerId !== event.pointerId) return;

        const target = event.currentTarget;
        if (
            target instanceof HTMLElement &&
            target.hasPointerCapture(event.pointerId)
        ) {
            target.releasePointerCapture(event.pointerId);
        }

        this.state.panState = null;
    }
    //#endregion

    //#region Private methods
    private downloadBlob(blob: Blob, fileName: string): void {
        const url = URL.createObjectURL(blob);
        this.downloadUrl(url, fileName);
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    private downloadUrl(
        url: string | null | undefined,
        fileName: string
    ): void {
        if (!url) return;

        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.rel = 'noopener noreferrer';
        anchor.click();
    }
    //#endregion
}
